import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import SimpleTextInput from '../../common/SimpleTextInput'
import Dropdown from '../../common/Dropdown'
import { toast } from '../../common/Toast'
import { productModelsWritePermissions } from '../../../utils/permissionValidation'
import { createProductModel, editProductModels, clearProductModelsMessages, loadAdminProductTypes } from '../../../actions/productModels'
import ToggleSwitch from '../../common/ToggleSwitch'
import TooltipedIcon from '../../common/TooltipedIcon'
import { InformationIcon } from '../../../icons'
import '../uploads.scss'

const normalizeModelPayload = (data = {}) => ({
  name: (data.name || '').trim(),
  productType: data.productType?.id ?? '',
  technology: data.technology?.id ?? '',
  dashboardAccess: boolFromAnything(data.dashboardAccess),
  coreUnit: boolFromAnything(data.coreUnit),
  tokenActivation: boolFromAnything(data.tokenActivation),
  status: boolFromAnything(data.isActive) ? 'active' : 'inactive',
})

const boolFromAnything = (v) => {
  if (typeof v === 'boolean') return v
  if (typeof v === 'string') return ['true', '1', 'y', 'yes'].includes(v.toLowerCase())
  if (typeof v === 'number') return v === 1
  return false
}

const AddEditProductModels = (props) => {
  const { t } = useTranslation()
  const { modelDetails: incoming, closeModal, showSpinner, setShowSpinner } = props
  const isEditMode = !!incoming?.id
  const [name, setName] = useState(incoming?.name || '')
  const [productType, setProductType] = useState(incoming?.productType?.id ?? '')
  const [category, setCategory] = useState(incoming?.category?.name || '')
  const [brand, setBrand] = useState(incoming?.brand?.name || '')
  const [technology, setTechnology] = useState(incoming?.technology?.id ?? '')
  const [status, setStatus] = useState(boolFromAnything(incoming?.isActive) ? 'active' : 'inactive')
  const [deviceFunction, setDeviceFunction] = useState(incoming?.deviceFunction?.name || '')
  const [dashboardAccess, setDashboardAccess] = useState(boolFromAnything(incoming?.dashboardAccess))
  const [coreUnits, setCoreUnits] = useState(boolFromAnything(incoming?.coreUnit))
  const [tokenActivation, setTokenActivation] = useState(boolFromAnything(incoming?.tokenActivation))
  const [haveError, setHaveError] = useState({})
  const [saving, setSaving] = useState(false)
  const [initialValues, setInitialValues] = useState(null)
  const boolStatuses = ['no', 'yes']
  const STATUSES = ['active', 'inactive']

  useEffect(() => {
    if (!incoming) return
    setName(incoming?.name || '')
    setProductType(incoming?.productType?.id ?? '')
    setTechnology(incoming?.technology?.id ?? '')
    setCategory(incoming?.category?.name || '')
    setBrand(incoming?.brand?.name || '')
    setDeviceFunction(incoming?.deviceFunction?.name || '')
    setStatus(boolFromAnything(incoming?.isActive) ? 'active' : 'inactive')
    setDashboardAccess(boolFromAnything(incoming?.dashboardAccess))
    setCoreUnits(boolFromAnything(incoming?.coreUnit))
    setTokenActivation(boolFromAnything(incoming?.tokenActivation))
    setHaveError({})

    setInitialValues(normalizeModelPayload(incoming))
  }, [incoming?.id])

  const isDirty = useMemo(() => {
    if (!isEditMode || !initialValues) return true
    const current = {
      name: name.trim(),
      productType,
      technology,
      dashboardAccess,
      coreUnit: coreUnits,
      tokenActivation,
      status,
    }

    return JSON.stringify(current) !== JSON.stringify(initialValues)
  }, [isEditMode, initialValues, name, productType, technology, status, dashboardAccess, coreUnits, tokenActivation])

  const productTypeOptions = useMemo(() => {
    return (props.productTypes || [])
      .filter((pt) => pt?.isActive)
      .map((pt) => ({
        key: pt?.id ?? '',
        value: pt?.productType ?? '',
      }))
      .filter((o) => o.key)
  }, [props.productTypes])

  const technologyOptions = useMemo(() => {
    return (props.technologies || [])
      .map((tech) => ({
        key: tech?.id ?? '',
        value: tech?.technology ?? '',
      }))
      .filter((o) => o.key)
  }, [props.technologies])

  const getProductTypeNameById = (id) => {
    const match = (props.productTypes || []).find((pt) => String(pt?.id) === String(id))
    return match?.productType || match?.name || ''
  }

  useEffect(() => {
    const hydrate = async () => {
      if (!productType) return

      const existing = (props.adminProductTypes || []).find((pt) => String(pt?.id) === String(productType))
      if (existing) {
        setCategory(existing?.category?.name || '')
        setBrand(existing?.brand?.name || '')
        setDeviceFunction(existing?.deviceFunction?.name || '')
        return
      }

      const productTypeName = getProductTypeNameById(productType)
      if (!productTypeName) return

      try {
        const items = await props.actions.loadAdminProductTypes(
          {
            productType: productTypeName,
            startPage: 1,
          },
          true,
        )

        const fetched = items?.value.data?.find((pt) => pt?.productType === productTypeName || pt?.productType?.name === productTypeName)
        setCategory(fetched?.category?.name || '')
        setBrand(fetched?.brand?.name || '')
        setDeviceFunction(fetched?.deviceFunction?.name || '')
      } catch (e) {
        console.log(e)
      }
    }
    hydrate()
  }, [productType])

  const adminProductTypeMap = useMemo(() => {
    const list = props.adminProductTypes || []
    const map = new Map()
    list.forEach((pt) => {
      const id = pt?.id
      if (id == null) return

      map.set(String(id), {
        category: pt?.category?.name || '',
        brand: pt?.brand?.name || '',
        deviceFunction: pt?.deviceFunction?.name || '',
      })
    })
    return map
  }, [props.adminProductTypes])

  useEffect(() => {
    if (!incoming) return
    setName(incoming?.name || '')
    setProductType(incoming?.productType?.id ?? '')
    setTechnology(incoming?.technology?.id ?? '')
    setCategory(incoming?.category?.name || '')
    setBrand(incoming?.brand?.name || '')
    setDeviceFunction(incoming?.deviceFunction?.name || '')
    setStatus(boolFromAnything(incoming?.isActive) ? 'active' : 'inactive')
    setDashboardAccess(boolFromAnything(incoming?.dashboardAccess))
    setCoreUnits(boolFromAnything(incoming?.coreUnit))
    setTokenActivation(boolFromAnything(incoming?.tokenActivation))
    setHaveError({})
  }, [incoming?.id])

  useEffect(() => {
    if (!productType) return
    const next = adminProductTypeMap.get(String(productType))
    setCategory(next?.category || '')
    setBrand(next?.brand || '')
    setDeviceFunction(next?.deviceFunction || '')
  }, [productType, adminProductTypeMap])

  const isValid = () => {
    const errors = {}
    let ok = true

    if (!name.trim()) {
      errors.name = true
      ok = false
    }

    if (!productType) {
      errors.productType = true
      ok = false
    }

    if (!technology) {
      errors.technology = true
      ok = false
    }

    if (!status) {
      errors.status = true
      ok = false
    }

    setHaveError(errors)
    return ok
  }

  const handleSave = async () => {
    if (!isValid()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    setSaving(true)
    setShowSpinner?.(true)

    props.actions.clearProductModelsMessages?.()

    const payload = {
      name: name.trim(),
      devicesMakeId: productType,
      technologyId: technology,
      dashboardAccess,
      coreUnit: coreUnits,
      tokenActivation,
      isActive: status === 'active',
    }

    let data = null

    try {
      const modelId = incoming?.id
      if (isEditMode) {
        data = await props.actions.editProductModels(modelId, payload)
      } else {
        data = await props.actions.createProductModel(payload)
      }
      closeModal()

      if (data?.code) {
        toast.error(t(`error.${data?.code}`))
        return
      }
    } catch (error) {
      if (error?.name) {
        toast.error(t(`error.${error.name}`))
        return
      }
      toast.error(t('error.something_went_wrong'))
    } finally {
      setSaving(false)
      setShowSpinner?.(false)
      props.actions.clearProductModelsMessages?.()
    }
  }

  const headerContent = () => (
    <div className="status-wrapper">
      <div className="label d-flex flex-justify-start flex-align-center">
        <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.model_status_info')}`} orientation="bottom" />
        {t('status')}
      </div>
      <Dropdown
        className="dropdown-input"
        options={STATUSES.map((s) => ({
          key: s,
          value: t(s),
        }))}
        initialSelectedKey={status}
        onChange={(opt) => setStatus(opt.key)}
        hasError={haveError.status}
      />
    </div>
  )

  return (
    <PageModal
      toggle
      onToggle={closeModal}
      className="uploads-modal models-wrapper"
      title={isEditMode ? t('product_models_context.edit_model') : t('product_models_context.add_model')}
      headerContent={headerContent()}
      footerContent={
        <>
          <SimpleButton className="cancel-button" onClick={closeModal}>
            {t('cancel')}
          </SimpleButton>
          <SimpleButton
            className="submit-button"
            onClick={handleSave}
            disabled={saving || !!showSpinner || (isEditMode && !isDirty)}
            requiredPermission={productModelsWritePermissions}
          >
            {saving ? t('saving') : t('save')}
          </SimpleButton>
        </>
      }
    >
      <div className="uploads-modal-content model-images-page">
        <div className="section-title">{t('product_models_context.general_model_details').toUpperCase()}</div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('model')}*`}</p>
            <SimpleTextInput value={name} onChange={setName} onClear={() => setName('')} hasError={haveError.name} />
          </div>
          <div className="input-wrapper">
            <div className="label d-flex flex-justify-start flex-align-center">
              <p className="input-option flex-justify-start">{`${t('product_type')}*`}</p>
              <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.type_status_info')}`} orientation="bottom" />
            </div>
            {productTypeOptions.length ? (
              <Dropdown
                placeholder={t('please_select')}
                options={productTypeOptions}
                initialSelectedKey={productType || undefined}
                onChange={(opt) => setProductType(opt.key)}
                hasError={haveError.productType}
              />
            ) : (
              <SimpleTextInput
                value={String(productType || '')}
                onChange={setProductType}
                onClear={() => setProductType('')}
                hasError={haveError.productType}
              />
            )}
          </div>
        </div>
        {isEditMode && (
          <>
            <div className="row-wrapper">
              <div className="input-wrapper">
                <p className="input-option">{`${t('category')}`}</p>
                <SimpleTextInput value={category} disabled={true} />
              </div>
              <div className="input-wrapper">
                <p className="input-option">{`${t('dealer-portal.brand')}`}</p>
                <SimpleTextInput value={brand} disabled={true} />
              </div>
            </div>
            <div className="row-wrapper">
              <div className="input-wrapper">
                <p className="input-option">{`${t('product_models_context.device_function')}`}</p>
                <SimpleTextInput value={deviceFunction} disabled={true} />
              </div>
              <div className="input-wrapper">
                <p className="input-option">{`${t('product_models_context.technology')}*`}</p>
                {technologyOptions.length ? (
                  <Dropdown
                    placeholder={t('please_select')}
                    options={technologyOptions}
                    initialSelectedKey={technology || undefined}
                    onChange={(opt) => setTechnology(opt.key)}
                    hasError={haveError.technology}
                  />
                ) : (
                  <SimpleTextInput
                    value={String(technology || '')}
                    onChange={setTechnology}
                    onClear={() => setTechnology('')}
                    hasError={haveError.technology}
                  />
                )}
              </div>
            </div>
          </>
        )}
        {!isEditMode && (
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('product_models_context.technology')}*`}</p>
              {technologyOptions.length ? (
                <Dropdown
                  placeholder={t('please_select')}
                  options={technologyOptions}
                  initialSelectedKey={technology || undefined}
                  onChange={(opt) => setTechnology(opt.key)}
                  hasError={haveError.technology}
                />
              ) : (
                <SimpleTextInput value={String(technology || '')} onChange={setTechnology} onClear={() => setTechnology('')} hasError={haveError.technology} />
              )}
            </div>
            <div className="input-wrapper"></div>
          </div>
        )}
        <div className="separator" />
        <div className="section-title">{t('product_models_context.website_access').toUpperCase()}</div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option flex-justify-start">
              {t('product_models_context.dashboard_access')}
              <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.dashboard_access_info')}`} orientation="extra-right" />
            </p>
            <div className="d-flex gap-8">
              <ToggleSwitch checked={boolFromAnything(dashboardAccess)} onChange={() => setDashboardAccess((prev) => !prev)} isDisabled={showSpinner} />
              <span className="status">{t(boolStatuses[Number(boolFromAnything(dashboardAccess))])}</span>
            </div>
          </div>
          <div className="input-wrapper">
            <p className="input-option flex-justify-start">
              {t('product_models_context.core_units')}
              <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.core_units_info')}`} orientation="right" />
            </p>
            <div className="d-flex gap-8">
              <ToggleSwitch checked={boolFromAnything(coreUnits)} onChange={() => setCoreUnits((prev) => !prev)} isDisabled={showSpinner} />
              <span className="status">{t(boolStatuses[Number(boolFromAnything(coreUnits))])}</span>
            </div>
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option flex-justify-start">
              {t('product_models_context.token_activation')}
              <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.token_activation_info')}`} orientation="extra-right" />
            </p>
            <div className="d-flex gap-8">
              <ToggleSwitch checked={boolFromAnything(tokenActivation)} onChange={() => setTokenActivation((prev) => !prev)} isDisabled={showSpinner} />
              <span className="status">{t(boolStatuses[Number(boolFromAnything(tokenActivation))])}</span>
            </div>
          </div>
          <div className="input-wrapper"></div>
        </div>
      </div>
    </PageModal>
  )
}

function stateToProps({ productModels, userPermissions }) {
  return {
    userPermissions,
    productTypes: productModels?.productModelsFilterOptions?.productTypes || [],
    productCategories: productModels?.productModelsFilterOptions?.productCategories || [],
    deviceFunctions: productModels?.productModelsFilterOptions?.deviceFunctions || [],
    technologies: productModels?.productModelsFilterOptions?.technologies || [],
    brands: productModels?.productModelsFilterOptions?.brands || [],
    adminProductTypes: productModels?.productTypes || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createProductModel,
        editProductModels,
        loadAdminProductTypes,
        clearProductModelsMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddEditProductModels)

